import React, { useState, useMemo } from 'react';
import _ from 'lodash';
import { post } from '../../util/api';
import { Form, Dropdown, Input, Button, Table, Checkbox, Message, Label } from 'semantic-ui-react';

// Kolajlardaki *_dropdown listelerine (size_dropdown, type_dropdown, ...) toplu değer ekler / siler.
// Her kolaj kendi yapısına göre güncellenir: listede batch kullanılıyorsa yeni kayda da batch eklenir, kullanılmıyorsa eklenmez.

const FIELD_LABELS = { size_dropdown: 'Beden', type_dropdown: 'Tip', color_dropdown: 'Renk' };

const norm = (v) =>
	String(v === undefined || v === null ? '' : v)
		.trim()
		.toUpperCase();

// Aynı değer başka bir kolajda batch ile tanımlıysa onu kullan, yoksa değerden tahmin et.
function suggestBatch(collages, field, value) {
	const key = norm(value);
	if (!key) return '';
	for (const c of collages) {
		const found = _.find(c.data && c.data[field], (it) => it && norm(it.range) === key && it.batch !== undefined);
		if (found) return String(found.batch);
	}
	const parts = key.split('-');
	if (parts.length === 2 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
		const diff = Number(parts[1]) - Number(parts[0]);
		if (diff > 0 && diff % 2 === 0) return String(diff / 2 + 1);
	}
	if (parts.length > 1 && parts.every((p) => /^[A-Z]+$/.test(p))) return String(parts.length);
	if (key === 'STANDART' || key === 'STD' || /^\d+$/.test(key)) return '1';
	return '';
}

function planFor(collage, field, value, op, batch) {
	const list = collage.data && collage.data[field];
	if (!Array.isArray(list)) return { status: 'missing' };
	const key = norm(value);
	const usesBatch = _.some(list, (it) => it && it.batch !== undefined);

	if (op === 'add') {
		if (_.some(list, (it) => it && norm(it.range) === key)) return { status: 'exists', usesBatch };
		const item = { range: String(value).trim() };
		if (usesBatch && batch !== '') item.batch = Number(batch);
		return { status: 'change', usesBatch, item, newList: [...list, item] };
	}

	const newList = _.filter(list, (it) => !(it && norm(it.range) === key));
	if (newList.length === list.length) return { status: 'notfound', usesBatch };
	return { status: 'change', usesBatch, removed: list.length - newList.length, newList };
}

export default function BulkCollageEdit({ collages, pid, onClose, onDone }) {
	const fieldOptions = useMemo(() => {
		const keys = _.union(
			_.keys(FIELD_LABELS),
			_.flatMap(collages, (c) => _.filter(_.keys(c.data), (k) => /_dropdown$/.test(k) && Array.isArray(c.data[k])))
		);
		return keys.map((k) => {
			const count = _.filter(collages, (c) => Array.isArray(c.data && c.data[k])).length;
			return { key: k, value: k, text: `${FIELD_LABELS[k] || k} (${k}) — ${count} kolajda var`, disabled: count === 0 };
		});
	}, [collages]);

	const [field, setField] = useState('size_dropdown');
	const [op, setOp] = useState('add');
	const [value, setValue] = useState('');
	const [batch, setBatch] = useState('');
	const [batchTouched, setBatchTouched] = useState(false);
	const [excluded, setExcluded] = useState({});
	const [saving, setSaving] = useState(false);
	const [result, setResult] = useState(null);

	const plans = useMemo(
		() => _.map(collages, (c) => ({ collage: c, plan: value.trim() ? planFor(c, field, value, op, batch) : null })),
		[collages, field, value, op, batch]
	);
	const targets = _.filter(plans, (p) => p.plan && p.plan.status === 'change' && !excluded[p.collage.id]);
	const needsBatch = op === 'add' && _.some(targets, (p) => p.plan.usesBatch);

	function onValueChange(v) {
		setValue(v);
		setResult(null);
		if (!batchTouched) setBatch(suggestBatch(collages, field, v));
	}

	async function apply() {
		if (!targets.length) return;
		if (needsBatch && batch === '' && !window.confirm('Batch kullanan kolajlara batch değeri olmadan eklenecek. Devam edilsin mi?')) return;
		const verb = op === 'add' ? 'eklenecek' : 'silinecek';
		if (!window.confirm(`"${value.trim()}" değeri ${targets.length} kolajda ${field} listesine ${verb}. Onaylıyor musunuz?`)) return;

		setSaving(true);
		const failed = [];
		for (const { collage, plan } of targets) {
			try {
				const res = await post('/shop/admin/schema/editing', { pid, id: collage.id, data: { ...collage.data, [field]: plan.newList } });
				if (!(res && res.data && res.data.success)) failed.push(collage.data.name);
			} catch (error) {
				console.error(error);
				failed.push(collage.data.name);
			}
		}
		setSaving(false);
		setResult({ ok: targets.length - failed.length, failed });
		setValue('');
		setBatchTouched(false);
		setBatch('');
		setExcluded({});
		if (onDone) await onDone();
	}

	function statusCell({ collage, plan }) {
		if (!plan) return null;
		switch (plan.status) {
			case 'missing':
				return <Label basic>Bu kolajda {field} yok — atlanır</Label>;
			case 'exists':
				return <Label basic>Zaten var — atlanır</Label>;
			case 'notfound':
				return <Label basic>Listede yok — atlanır</Label>;
			default:
				if (op === 'remove') return <Label color="red">{plan.removed} kayıt silinecek</Label>;
				return (
					<Label color="green">
						Eklenecek: {JSON.stringify(plan.item)}
						{plan.usesBatch && batch === '' ? ' (batch eksik!)' : ''}
					</Label>
				);
		}
	}

	return (
		<div>
			<Form>
				<Form.Group widths="equal">
					<Form.Field>
						<label>Alan</label>
						<Dropdown
							selection
							options={fieldOptions}
							value={field}
							onChange={(e, { value: v }) => {
								setField(v);
								setResult(null);
								if (!batchTouched) setBatch(suggestBatch(collages, v, value));
							}}
						/>
					</Form.Field>
					<Form.Field>
						<label>İşlem</label>
						<Dropdown
							selection
							options={[
								{ key: 'add', value: 'add', text: 'Hepsine ekle' },
								{ key: 'remove', value: 'remove', text: 'Hepsinden sil' },
							]}
							value={op}
							onChange={(e, { value: v }) => setOp(v)}
						/>
					</Form.Field>
					<Form.Field>
						<label>Değer</label>
						<Input placeholder="örn. 52-58, S-M, DRESS" value={value} onChange={(e, { value: v }) => onValueChange(v)} />
					</Form.Field>
					{op === 'add' && (
						<Form.Field>
							<label>Batch (sadece batch kullanan kolajlara)</label>
							<Input
								type="number"
								placeholder="boş = batch yok"
								value={batch}
								onChange={(e, { value: v }) => {
									setBatch(v);
									setBatchTouched(true);
								}}
							/>
						</Form.Field>
					)}
				</Form.Group>
			</Form>

			{result && (
				<Message positive={!result.failed.length} negative={!!result.failed.length}>
					{result.ok} kolaj güncellendi.
					{result.failed.length ? ` Hata alanlar: ${result.failed.join(', ')}` : ''}
				</Message>
			)}

			{value.trim() ? (
				<Table celled compact style={{ width: '99%' }}>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell collapsing />
							<Table.HeaderCell>Kolaj</Table.HeaderCell>
							<Table.HeaderCell>Batch kullanıyor</Table.HeaderCell>
							<Table.HeaderCell>Sonuç</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{_.map(plans, (p) => {
							const changeable = p.plan && p.plan.status === 'change';
							return (
								<Table.Row key={p.collage.id} disabled={!changeable}>
									<Table.Cell>
										<Checkbox
											disabled={!changeable}
											checked={changeable && !excluded[p.collage.id]}
											onChange={(e, { checked }) => setExcluded({ ...excluded, [p.collage.id]: !checked })}
										/>
									</Table.Cell>
									<Table.Cell>{p.collage.data.name}</Table.Cell>
									<Table.Cell>{p.plan && p.plan.status !== 'missing' ? (p.plan.usesBatch ? 'Evet' : 'Hayır') : '-'}</Table.Cell>
									<Table.Cell>{statusCell(p)}</Table.Cell>
								</Table.Row>
							);
						})}
					</Table.Body>
				</Table>
			) : (
				<Message info>Alanı ve değeri girin; hangi kolajlara nasıl ekleneceğinin önizlemesi burada görünecek.</Message>
			)}

			<div style={{ textAlign: 'right', marginTop: 10 }}>
				<Button color="orange" disabled={saving} onClick={onClose}>
					Geri
				</Button>
				<Button
					color="facebook"
					icon="checkmark"
					content={`Uygula (${targets.length} kolaj)`}
					loading={saving}
					disabled={saving || !targets.length}
					onClick={apply}
				/>
			</div>
		</div>
	);
}
